import { useEffect, useState } from "react";
import api, { formatIDR, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import RupiahInput from "@/components/RupiahInput";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, Wrench } from "@phosphor-icons/react";

const CATEGORIES = [
  { value: "perbaikan", label: "Perbaikan" },
  { value: "servis", label: "Servis Rutin" },
  { value: "sparepart", label: "Sparepart" },
  { value: "dokumen", label: "Dokumen / STNK" },
  { value: "poles", label: "Poles / Detailing" },
  { value: "lainnya", label: "Lainnya" },
];

const emptyForm = {
  category: "perbaikan",
  description: "",
  amount: "",
  expense_date: new Date().toISOString().slice(0, 10),
};

export default function CarExpensesDialog({ car, open, onOpenChange, onChange }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!car) return;
    api.get(`/cars/${car.id}/expenses`).then((r) => setItems(r.data));
  };

  useEffect(() => {
    if (open && car) {
      setForm(emptyForm);
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, car]);

  const total = items.reduce((a, b) => a + Number(b.amount || 0), 0);

  const submit = async () => {
    const amt = parseFloat(form.amount || 0);
    if (!amt || amt <= 0) { toast.error("Jumlah biaya harus lebih dari 0"); return; }
    setBusy(true);
    try {
      await api.post(`/cars/${car.id}/expenses`, {
        category: form.category,
        description: form.description,
        amount: amt,
        expense_date: form.expense_date,
      });
      toast.success("Biaya dicatat");
      setForm(emptyForm);
      load();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menambah biaya");
    } finally { setBusy(false); }
  };

  const del = async (eid) => {
    if (!window.confirm("Hapus biaya ini?")) return;
    try {
      await api.delete(`/cars/${car.id}/expenses/${eid}`);
      toast.success("Biaya dihapus");
      load();
      onChange && onChange();
    } catch (e) { toast.error("Gagal menghapus"); }
  };

  if (!car) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Wrench size={18} weight="bold" /> Biaya Mobil
          </DialogTitle>
        </DialogHeader>

        <div className="border border-gray-200 p-4 bg-gray-50">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-mono-num font-semibold text-lg">{car.account_number || "-"}</div>
              <div className="font-semibold mt-1">{car.brand} {car.model} {car.type ? "· " + car.type : ""}</div>
              <div className="text-xs text-gray-500 font-mono-num">{car.year} · {car.plate_number || "no plat"} · {car.color || "-"}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-medium">Total Biaya</div>
              <div className="font-mono-num font-bold text-lg text-[#F97316]" data-testid="expense-total">{formatIDR(total)}</div>
              <div className="text-[11px] text-gray-500 font-mono-num">{items.length} catatan</div>
            </div>
          </div>
        </div>

        {/* Add form */}
        <div className="border border-gray-200 p-4">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-medium mb-3">Tambah Biaya</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="expense-cat-trigger" className="mt-2 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Tanggal</Label>
              <Input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} className="mt-2 rounded-none" data-testid="expense-date" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider">Keterangan</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Contoh: Ganti kampas rem depan" className="mt-2 rounded-none" data-testid="expense-desc" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider">Jumlah</Label>
              <RupiahInput value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-2 rounded-none" data-testid="expense-amount" />
            </div>
          </div>
          <Button onClick={submit} disabled={busy} data-testid="expense-submit" className="mt-4 w-full rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-11">
            <Plus size={14} className="mr-2" /> {busy ? "Menyimpan..." : "Tambah Biaya"}
          </Button>
        </div>

        {/* List */}
        <div className="border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                <th className="p-3">Tanggal</th>
                <th className="p-3">Kategori</th>
                <th className="p-3">Keterangan</th>
                <th className="p-3 text-right">Jumlah</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-gray-100" data-testid={`expense-row-${it.id}`}>
                  <td className="p-3 font-mono-num text-xs">{formatDate(it.expense_date)}</td>
                  <td className="p-3">
                    <span className="inline-block px-2 py-1 text-[10px] uppercase tracking-wider border border-gray-900">
                      {CATEGORIES.find((c) => c.value === it.category)?.label || it.category}
                    </span>
                  </td>
                  <td className="p-3 text-gray-700">{it.description || <span className="text-gray-400 italic">tanpa keterangan</span>}</td>
                  <td className="p-3 text-right font-mono-num font-semibold">{formatIDR(it.amount)}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => del(it.id)} data-testid={`expense-del-${it.id}`} className="text-gray-500 hover:text-[#E63946]"><Trash size={14} /></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan="5" className="p-8 text-center text-sm text-gray-500">Belum ada biaya tercatat untuk mobil ini</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
